import { Injectable } from '@angular/core'
import {
  SiteAnnouncementsEntity,
  SiteAnnouncementsFacade,
  TargetTypeEnum,
} from '@galabid/gb-dashboard/staff/data-access-site-announcements'
import { AccountFacade } from 'libs/gb-dashboard/account/data-access/src/lib/+state/account.facade'
import { ClientAccountTypeEnum } from 'libs/gb-dashboard/account/data-access/src/lib/+state/account.models'
import { GbDashboardAuthService } from 'libs/gb-dashboard/account/utils/src/lib/gb-dashboard-auth-service/gb-dashboard-auth.service'
import {
  GB_SEEN_ANNOUCEMENTS,
  GbLocalStorageService,
} from 'libs/gb-dashboard/shared/utils/src/lib/gb-localstorage-service/gb-localstorage.service'
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class FilteredAnnouncementsService {
  siteAnnouncementsReadStatusMapSubject$ = new BehaviorSubject<{
    [key: string]: { list: boolean; dialog: boolean }
  }>(this.getSiteAnnouncementsReadStatusMap())

  filteredAnnouncements$: Observable<SiteAnnouncementsEntity[]> = combineLatest([
    this.siteAnnouncementsFacade.displayedSiteAnnouncements$,
    this.accountFacade.clientAccountsType$,
  ]).pipe(
    map(([announcements, clientAccountsType]) => {
      const allowedTargets = this.getAllowedTargets(clientAccountsType)

      return [...announcements]
        .sort((a, b) => new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime())
        .filter((a) => allowedTargets.includes(a.target))
    }),
  )

  unreadDialogAnnouncements$: Observable<SiteAnnouncementsEntity[]> = this.filteredAnnouncements$.pipe(
    map((filteredAnnouncements) => {
      const readStatusMap = this.getSiteAnnouncementsReadStatusMap()
      return filteredAnnouncements.filter((filteredAnnouncement) => !readStatusMap[filteredAnnouncement.uuid]?.dialog)
    }),
  )

  unreadListAnnouncements$: Observable<SiteAnnouncementsEntity[]> = combineLatest([
    this.filteredAnnouncements$,
    this.siteAnnouncementsReadStatusMapSubject$,
  ]).pipe(
    map(([filteredAnnouncements, siteAnnouncementsListReadStatusMap]) => {
      const readStatusMap = siteAnnouncementsListReadStatusMap
      return filteredAnnouncements.filter((filteredAnnouncement) => !readStatusMap[filteredAnnouncement.uuid]?.list)
    }),
  )

  constructor(
    private siteAnnouncementsFacade: SiteAnnouncementsFacade,
    private accountFacade: AccountFacade,
    private gbLocalStorageService: GbLocalStorageService,
    private gbDashboardAuthService: GbDashboardAuthService,
  ) {}

  getSiteAnnouncementsReadStatusMap(): { [key: string]: { list: boolean; dialog: boolean } } {
    return this.gbLocalStorageService.getItem(GB_SEEN_ANNOUCEMENTS) || {}
  }

  markListAnnouncementAsRead(announcement: SiteAnnouncementsEntity): void {
    let readStatusMap = this.getSiteAnnouncementsReadStatusMap()
    readStatusMap[announcement.uuid] = { ...(readStatusMap[announcement.uuid] || {}), list: true }
    this.gbLocalStorageService.setItem(GB_SEEN_ANNOUCEMENTS, readStatusMap)
    this.siteAnnouncementsReadStatusMapSubject$.next(readStatusMap)
  }

  private getAllowedTargets(clientAccountsType: ClientAccountTypeEnum | ''): TargetTypeEnum[] {
    if (this.gbDashboardAuthService.isStaffSuperUserDistributor()) {
      return [TargetTypeEnum.All, TargetTypeEnum.Saas, TargetTypeEnum.NonSaas]
    }

    switch (clientAccountsType) {
      case ClientAccountTypeEnum.Both:
        return [TargetTypeEnum.All, TargetTypeEnum.Saas, TargetTypeEnum.NonSaas]
      case ClientAccountTypeEnum.Saas:
        return [TargetTypeEnum.All, TargetTypeEnum.Saas]
      case ClientAccountTypeEnum.NonSaas:
        return [TargetTypeEnum.All, TargetTypeEnum.NonSaas]
      default:
        return [TargetTypeEnum.All]
    }
  }
}
